/**
 * Fix for single-variant products in quick view
 * Prevents "Unavailable" button state when changing quantity
 *
 * Also preserves size selection when switching color swatches.
 */
(function() {
  'use strict';

  // Store initialized forms to prevent duplicate processing
  const initializedForms = new WeakSet();

  /**
   * Initialize fix for a single-variant product form
   */
  function initFixForForm(form) {
    if (initializedForms.has(form)) {
      return;
    }

    const hiddenSelect = form.querySelector('.js-quick-view-hidden-select');
    const submitBtn = form.querySelector('.js-quick-submit-button');

    if (!hiddenSelect || !submitBtn) {
      return;
    }

    // Get all options from the hidden select
    const options = Array.from(hiddenSelect.querySelectorAll('option'));

    // Check if this is a single-variant product
    if (options.length !== 1) {
      return; // Multiple variants, let normal behavior work
    }

    const variant = {
      id: options[0].value,
      available: !submitBtn.disabled
    };

    // Only proceed if variant is available
    if (!variant.available) {
      return;
    }

    // Mark as initialized
    initializedForms.add(form);

    // Ensure hidden select always has the variant ID
    hiddenSelect.value = variant.id;

    // Function to restore button state
    function restoreButtonState() {
      const isPreorder = submitBtn.hasAttribute('data-preorder');
      const addToCartText = submitBtn.getAttribute('data-add-to-card') ||
                            submitBtn.getAttribute('data-add-to-cart') ||
                            'Add to bag';

      if (submitBtn.disabled) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('disabled');
      }

      if (submitBtn.innerText !== addToCartText && submitBtn.innerText.toLowerCase().includes('unavailable')) {
        submitBtn.innerText = addToCartText;
      }
    }

    // Intercept change events in capture phase (before main handler)
    form.addEventListener('change', function(e) {
      // Check if this is a quantity-related change
      const isQuantityChange = e.target.matches('[data-quantity-input]') ||
                               e.target.closest('[data-quantity-input]') ||
                               e.target.matches('.product-quantity__input');

      if (isQuantityChange) {
        // Ensure the hidden select value is maintained
        hiddenSelect.value = variant.id;

        // Stop propagation to prevent main handler from running
        e.stopImmediatePropagation();

        // Restore button state after a short delay
        setTimeout(restoreButtonState, 0);
      }
    }, true); // Capture phase is crucial

    // Monitor button for any changes
    const observer = new MutationObserver(function(mutations) {
      for (let mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          if (submitBtn.disabled) {
            restoreButtonState();
          }
        }
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          if (submitBtn.innerText.toLowerCase().includes('unavailable')) {
            restoreButtonState();
          }
        }
      }
    });

    observer.observe(submitBtn, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      characterData: true,
      subtree: true
    });

    // Intercept clicks on quantity buttons
    const quantityBtns = form.querySelectorAll('[data-quantity-increase], [data-quantity-decrease]');
    quantityBtns.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        hiddenSelect.value = variant.id;
        setTimeout(restoreButtonState, 0);
      }, true);
    });
  }

  /**
   * Check all quick view forms on the page
   */
  function checkForQuickViewForms() {
    const forms = document.querySelectorAll('.js-quick-view-form');
    forms.forEach(initFixForForm);
  }

  // Watch for quick view forms being added to the DOM
  const bodyObserver = new MutationObserver(function(mutations) {
    for (let mutation of mutations) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself is a quick view
            if (node.matches && node.matches('.js-quick-view')) {
              setTimeout(checkForQuickViewForms, 10);
            }
            // Check if any descendants are quick view forms
            if (node.querySelector) {
              const form = node.querySelector('.js-quick-view-form');
              if (form) {
                setTimeout(checkForQuickViewForms, 10);
              }
            }
          }
        });
      }
    }
  });

  // Start observing the body for changes
  if (document.body) {
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  } else {
    document.addEventListener('DOMContentLoaded', function() {
      bodyObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

  // Check for existing forms on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkForQuickViewForms);
  } else {
    checkForQuickViewForms();
  }

  // ---------------------------------------------------------------------------
  // Size preservation: keep the selected size when switching color swatches
  // ---------------------------------------------------------------------------

  const initializedSizeForms = new WeakSet();

  /**
   * Reads the color option name configured in theme settings.
   * The value is embedded by quick-view-form.liquid via data-option-name attributes.
   * We derive the color position by matching against the swatch radio's data-option-name.
   */
  function initSizePreservation(form) {
    if (initializedSizeForms.has(form)) return;

    const hiddenSelect = form.querySelector('.js-quick-view-hidden-select');
    if (!hiddenSelect) return;

    // Build option position -> lowercase name map from annotated radio inputs
    const optionNameByPos = {};
    form.querySelectorAll('.radio-input[data-option][data-option-name]').forEach(function(input) {
      const pos = input.getAttribute('data-option');       // e.g. "option1"
      const name = input.getAttribute('data-option-name'); // e.g. "color"
      if (pos && name) optionNameByPos[pos] = name;
    });

    // Identify color position (exact match against theme setting) and size position
    // The theme's swatch option name is stored as data-option-name on each swatch input.
    // We find the color key by looking for any non-size option that isn't size/age.
    // More reliably: colour inputs carry class "js-swatch"; use that to find colorPos.
    let colorPos = null;
    let sizePos = null;

    const swatchInput = form.querySelector('.radio-input.js-swatch[data-option]');
    if (swatchInput) {
      colorPos = swatchInput.getAttribute('data-option');
    }

    Object.keys(optionNameByPos).forEach(function(pos) {
      if (pos === colorPos) return;
      const name = optionNameByPos[pos];
      if (name.indexOf('size') !== -1 || name.indexOf('age') !== -1) {
        sizePos = pos;
      }
    });

    // Fallback: if still not found, pick the second non-color option position
    if (!sizePos) {
      const positions = Object.keys(optionNameByPos).filter(p => p !== colorPos);
      sizePos = positions[0] || null;
    }

    if (!colorPos || !sizePos) return;

    // Parse all variants from the hidden select (needs data-available on each <option>)
    const variants = Array.from(hiddenSelect.querySelectorAll('option')).map(function(opt) {
      return {
        option1: opt.getAttribute('data-option1'),
        option2: opt.getAttribute('data-option2'),
        option3: opt.getAttribute('data-option3'),
        available: opt.getAttribute('data-available') === 'true',
      };
    });

    const sizeIdx = parseInt(sizePos.replace('option', ''), 10) - 1;
    const optionKeys = ['option1', 'option2', 'option3'];

    let lastSelectedSize = null;
    initializedSizeForms.add(form);

    function getSelectedForOption(pos) {
      const checked = form.querySelector('.radio-input[data-option="' + pos + '"]:checked');
      if (checked) return checked.value;
      const sel = form.querySelector('select[data-option="' + pos + '"]');
      return sel ? sel.value : null;
    }

    function buildSelectedValues() {
      return optionKeys.map(function(_key, i) {
        return getSelectedForOption('option' + (i + 1)) || null;
      });
    }

    function findVariantForValues(values) {
      return variants.find(function(v) {
        return optionKeys.every(function(key, i) {
          return !values[i] || v[key] === values[i];
        });
      });
    }

    function updateSizeStates(preserveSize) {
      const selectedValues = buildSelectedValues();
      const sizeInputs = form.querySelectorAll('.radio-input[data-option="' + sizePos + '"]');

      sizeInputs.forEach(function(input) {
        const testValues = selectedValues.slice();
        testValues[sizeIdx] = input.value;

        const matching = findVariantForValues(testValues);
        const isAvailable = !!(matching && matching.available);
        const label = form.querySelector('label[for="' + input.id + '"]');

        if (!isAvailable) {
          input.disabled = true;
          if (label) label.classList.add('is-disabled');
        } else {
          input.disabled = false;
          if (label) label.classList.remove('is-disabled');
        }
      });

      if (!preserveSize) return;

      // Try to restore the previously selected size
      let targetInput = null;
      sizeInputs.forEach(function(input) {
        if (input.value === lastSelectedSize && !input.disabled) targetInput = input;
      });

      // Fall back to the first available size
      if (!targetInput) {
        sizeInputs.forEach(function(input) {
          if (!input.disabled && !targetInput) targetInput = input;
        });
      }

      if (targetInput && !targetInput.checked) {
        targetInput.checked = true;
        targetInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    form.querySelectorAll('.radio-input[data-option]').forEach(function(input) {
      input.addEventListener('change', function(e) {
        const changedOption = e.target.getAttribute('data-option');
        if (changedOption === sizePos) {
          // User explicitly changed the size — track it
          lastSelectedSize = e.target.value;
          updateSizeStates(false);
        } else {
          // Color (or other non-size) changed — restore the preserved size
          updateSizeStates(true);
        }
      });
    });

    // Set initial availability states
    updateSizeStates(false);
  }

  function checkForSizePreservationForms() {
    document.querySelectorAll('.js-quick-view-form').forEach(initSizePreservation);
  }

  // Reuse the existing bodyObserver by also calling checkForSizePreservationForms
  // when quick view forms appear. We piggyback on the already-running MutationObserver
  // by observing the same body-level mutations via a second observer.
  const sizeBodyObserver = new MutationObserver(function(mutations) {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          const form = (node.matches && node.matches('.js-quick-view-form'))
            ? node
            : node.querySelector && node.querySelector('.js-quick-view-form');
          if (form) setTimeout(checkForSizePreservationForms, 10);
        });
      }
    }
  });

  if (document.body) {
    sizeBodyObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function() {
      sizeBodyObserver.observe(document.body, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkForSizePreservationForms);
  } else {
    checkForSizePreservationForms();
  }

})();
